import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { getCommerceId } from '@/lib/ownership';
import {
  getOrganizationByCommerce,
  listOrganizationMemberships,
  listRolesForOrganization,
  fetchUserLabels,
  type CoreMembership,
  type CoreOrganization,
  type CoreRole,
  type UserLabel,
} from '@/lib/zifekCore';
import TeamAccessTab from './components/TeamAccessTab';
import ActivityLogTab from './components/ActivityLogTab';
import { fetchRecentActionCounts } from '@/lib/coreAudit';

type Tab = 'team' | 'activity';

export default function DashboardUsersPage() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [tab, setTab] = useState<Tab>('team');
  const [org, setOrg] = useState<CoreOrganization | null>(null);
  const [memberships, setMemberships] = useState<CoreMembership[]>([]);
  const [roles, setRoles] = useState<CoreRole[]>([]);
  const [users, setUsers] = useState<Record<number, UserLabel>>({});
  const [actionCounts, setActionCounts] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const commerceId = useMemo(() => getCommerceId(user), [user]);
  const allowed = has(['members.read', 'members.manage', 'roles.manage']);

  const load = useCallback(async () => {
    if (!commerceId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const organization = await getOrganizationByCommerce(commerceId);
      setOrg(organization);
      if (!organization) { setLoading(false); return; }

      const [memb, rl] = await Promise.all([
        listOrganizationMemberships(organization.id),
        listRolesForOrganization(organization.id),
      ]);
      setMemberships(memb);
      setRoles(rl);
      const labels = await fetchUserLabels([...memb.map((m) => m.user_id), organization.owner_user_id || 0]);
      setUsers(labels);
      // Compteur d'actions récentes par travailleur (30 derniers jours).
      setActionCounts(await fetchRecentActionCounts(organization.id, 30));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Impossible de charger la gestion d'utilisateurs.");
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: memberships.length,
    active: memberships.filter((m) => m.status === 'active').length,
    suspended: memberships.filter((m) => m.status !== 'active').length,
    roles: roles.length,
  }), [memberships, roles]);

  if (!allowed) {
    return (
      <div className="p-4 md:p-6">
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-lock-2-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-700 font-medium">Accès restreint.</p>
          <p className="text-sm text-foreground-500 mt-1 max-w-md">Seul le propriétaire ou un gestionnaire peut administrer les utilisateurs et leurs accès.</p>
        </div>
      </div>
    );
  }

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'team', label: 'Équipe & accès', icon: 'ri-group-line' },
    { key: 'activity', label: "Journal d'activité", icon: 'ri-history-line' },
  ];

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-user-settings-line text-primary-600"></i>
            Gestion d'utilisateurs
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Ajoutez vos travailleurs, donnez-leur un accès précis par application, et suivez toutes leurs actions.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 disabled:opacity-50 transition-colors"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i></div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><i className="ri-error-warning-line"></i>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      ) : !org ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-building-2-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-700 font-medium">Votre organisation n'est pas encore initialisée.</p>
          <p className="text-sm text-foreground-500 mt-1 max-w-md">Le socle de votre entreprise est normalement créé automatiquement. Contactez le support si le problème persiste.</p>
          <button onClick={load} className="mt-4 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2 bg-primary-100 text-primary-700"><i className="ri-group-line text-lg"></i></div>
              <div className="text-xl font-bold font-heading text-foreground-950">{stats.total}</div>
              <div className="text-xs text-foreground-500">Travailleurs</div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2 bg-accent-100 text-accent-900"><i className="ri-user-follow-line text-lg"></i></div>
              <div className="text-xl font-bold font-heading text-foreground-950">{stats.active}</div>
              <div className="text-xs text-foreground-500">Actifs</div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2 bg-secondary-100 text-secondary-700"><i className="ri-user-unfollow-line text-lg"></i></div>
              <div className="text-xl font-bold font-heading text-foreground-950">{stats.suspended}</div>
              <div className="text-xs text-foreground-500">Suspendus</div>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-2 bg-foreground-100 text-foreground-700"><i className="ri-shield-user-line text-lg"></i></div>
              <div className="text-xl font-bold font-heading text-foreground-950">{stats.roles}</div>
              <div className="text-xs text-foreground-500">Rôles disponibles</div>
            </div>
          </div>

          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 mb-5 w-full sm:w-fit overflow-x-auto">
            {tabs.map((tb) => (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${tab === tb.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
              >
                <i className={tb.icon}></i>
                {tb.label}
              </button>
            ))}
          </div>

          {tab === 'team' && (
            <TeamAccessTab
              org={org}
              memberships={memberships}
              roles={roles}
              users={users}
              actorName={user?.name ?? null}
              actorId={user?.id ?? null}
              ownerType={user?.type ?? null}
              ownerTypecompte={user?.typecompte ?? null}
              commerceId={commerceId}
              currency={user?.monaie || 'MAD'}
              actionCounts={actionCounts}
              onChanged={load}
            />
          )}
          {tab === 'activity' && <ActivityLogTab org={org} users={users} memberships={memberships} />}
        </>
      )}
    </div>
  );
}